import {
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { useQuery, useQueries } from "react-query";
import { Flex, Switch, Spinner, Text } from "@radix-ui/themes";
import { params } from "@/params";
import { apiResolver, useApi } from "@/api/useApi";
import { FeatureAccessContext } from "@/context/featureAccess";
import { Message } from "@/components/quack-chat/core/entities/Message";
import { Messages } from "@/components/quack-chat/core/parts/message/Messages";
import { WalkthroughProvider } from "@/components/quack-chat/core/parts/WalkthroughContext";
import { ChatSettings } from "@/components/quack-chat/QuackChatConfig.model";
import { defaultChatSettings } from "@/components/quack-chat/QuackChat";
import { ExploreInteractionRow } from "@/v2/entities/explore/Explore.model";
import { useQuackchatPlatformConfiguration } from "@/api/quackchat/configuration/useQuackchatPlatformConfiguration";
import { createPortal } from "react-dom";
import { CommentSources, Source } from "@/v2/components/messageSources";
import { TrainMessage, TrainMessageUpTo } from "@/v2/components/trainMessage";
import { useFlag } from "@/v2/components/feature-flag/FeatureFlagContext";
import { ChainOfThoughtsButton } from "@/v2/components/chainOfThoughtsButton";
import { BrainIcon } from "lucide-react";
import { useSuggestedReplyAuiApi } from "@/v2/entities/suggested-reply-aui/SuggestedReplyAui.api";
import { AuiChatMessage } from "@/v2/entities/suggested-reply-aui/SuggestedReplyAui.model";
import { Tooltip } from "@/components/shared/tooltip";
import { PiTranslate } from "react-icons/pi";
import { translateText } from "@/services/translation";
import { toast } from "sonner";


export function MessageActions({
  chatMessageId,
  source,
  isAgent,
  onTrain,
  onTrainUpTo,
  isFirst,
  onChainOfThoughtsClick,
  isAuiMock,
  onToggleAuiMock,
  hasAuiResponse,
}: {
  chatMessageId: string;
  source?: Source;
  isAgent: boolean;
  onTrain?: () => void;
  onTrainUpTo?: () => void;
  isFirst?: boolean;
  onChainOfThoughtsClick?: (source: any) => void;
  isAuiMock?: boolean;
  onToggleAuiMock?: () => void;
  hasAuiResponse?: boolean;
}) {
  const [elem, setElem] = useState<HTMLElement | null>(null);
  const [footerElem, setFooterElem] = useState<HTMLElement | null>(null);
  const suggestedReplyAui = useFlag("suggestedReplyAui", false);

  useEffect(() => {
    const el = document.getElementById(chatMessageId);
    if (el && !el.style.position) {
      el.style.position = "relative";
    }
    setElem(el);

    const footer = document.querySelector(
      `[data-message-footer="${chatMessageId}"]`
    ) as HTMLElement | null;
    setFooterElem(footer);
  }, [chatMessageId]);

  const showAuiToggle = useMemo(
    () =>
      suggestedReplyAui &&
      source?.chainOfThoughtSteps &&
      source.chainOfThoughtSteps.length > 0,
    [suggestedReplyAui, source]
  );

  const handleAuiToggleChange = useCallback(() => {
    onToggleAuiMock?.();
  }, [onToggleAuiMock]);

  const auiSwitch = useMemo(() => {
    if (!showAuiToggle || !onToggleAuiMock) return null;
    const switchEl = (
      <Switch
        size="1"
        checked={isAuiMock ?? false}
        onCheckedChange={handleAuiToggleChange}
        disabled={!hasAuiResponse}
      />
    );
    if (!hasAuiResponse) {
      return (
        <Tooltip>
          <Tooltip.Trigger>
            <span style={{ display: "inline-flex" }}>{switchEl}</span>
          </Tooltip.Trigger>
          <Tooltip.Content>No response generated</Tooltip.Content>
        </Tooltip>
      );
    }
    return switchEl;
  }, [
    showAuiToggle,
    onToggleAuiMock,
    handleAuiToggleChange,
    isAuiMock,
    hasAuiResponse,
  ]);

  const showBrainIcon =
    onChainOfThoughtsClick &&
    source?.chainOfThoughtSteps &&
    source.chainOfThoughtSteps.length > 0 &&
    isAgent;

  if (elem == null || (!source && !onTrain && !onTrainUpTo)) {
    return <></>;
  }

  return (
    <>
      {createPortal(
        <div
          style={{
            position: "absolute",
            bottom: "-30px",
            right: isAgent ? "0px" : "82px",
          }}
        >
          {!onTrain && !onTrainUpTo ? (
            <Flex gap="2" align="center">
              {source && (
                <>
                  <ChainOfThoughtsButton
                    source={source}
                    onClick={() => onChainOfThoughtsClick?.(source)}
                  />
                  {showAuiToggle && (
                    <Flex align="center" gap="1">
                      <Text size="1" color="gray">
                        AUI
                      </Text>
                      {auiSwitch}
                    </Flex>
                  )}
                </>
              )}
            </Flex>
          ) : (
            <Flex gap="2" align="center">
              {onTrainUpTo && isFirst && !isAgent && (
                <TrainMessageUpTo onTrain={onTrainUpTo} />
              )}
              {onTrain && !isAgent && <TrainMessage onTrain={onTrain} />}
            </Flex>
          )}
        </div>,
        elem
      )}
      {showBrainIcon &&
        footerElem &&
        createPortal(
          <Tooltip>
            <Tooltip.Trigger>
              <button
                onClick={() => onChainOfThoughtsClick(source!)}
                style={{
                  background: "none",
                  border: "1px solid var(--gray-7)",
                  borderRadius: "4px",
                  cursor: "pointer",
                  padding: "3px",
                  display: "inline-flex",
                  alignItems: "center",
                  color: "var(--gray-11)",
                }}
              >
                <BrainIcon size={14} />
              </button>
            </Tooltip.Trigger>
            <Tooltip.Content>View chain of thought</Tooltip.Content>
          </Tooltip>,
          footerElem
        )}
    </>
  );
}

interface ConversationTranscriptProps {
  conversation: Pick<ExploreInteractionRow, "id" | "displayId">;
  simulatorId?: string;
  onTrain?: (messageId: string, message: string) => void;
  chooseMessagesUpTo?: (
    targetMessageId: string,
    messages: Array<{ chatMessageId: string; text: string; author: string }>
  ) => void;
  onChainOfThoughtsClick?: (source: any) => void;
  onMessagesLoaded?: (messages: any[]) => void;
}

export function ConversationTranscript({
  conversation,

  onTrain,
  chooseMessagesUpTo,
  onChainOfThoughtsClick,
  onMessagesLoaded,
}: ConversationTranscriptProps) {
  const { selectedWorkspace } = useContext(FeatureAccessContext);
  const { api } = useApi();
  const { data: configuration, isLoading } =
    useQuackchatPlatformConfiguration();

  const suggestedReplyAui = useFlag("suggestedReplyAui", false);

  const [translateAll, setTranslateAll] = useState(false);
  const [translations, setTranslations] = useState<Map<string, string>>(
    new Map()
  );
  const [isBatchTranslating, setIsBatchTranslating] = useState(false);
  const [auiMockMessageIds, setAuiMockMessageIds] = useState<Set<string>>(
    new Set()
  );

  const toggleAuiMock = useCallback((messageId: string) => {
    setAuiMockMessageIds((prev) => {
      const next = new Set(prev);
      if (next.has(messageId)) {
        next.delete(messageId);
      } else {
        next.add(messageId);
      }
      return next;
    });
  }, []);

  // Translation state management

  const scrollContainerRef = useRef<HTMLDivElement | null>(null);
  const scrollPositionRef = useRef<number>(0);

  // Find and store reference to scroll container
  useEffect(() => {
    const findScrollContainer = () => {
      // The Messages component creates a scrollable div, let's find it
      const scrollableDiv = document.querySelector(
        "[data-radix-scroll-area-viewport]"
      ) as HTMLDivElement;
      if (scrollableDiv) {
        scrollContainerRef.current = scrollableDiv;
      }
    };

    // Try to find it after a short delay to ensure Messages component has rendered
    const timeout = setTimeout(findScrollContainer, 100);
    return () => clearTimeout(timeout);
  }, []);

  const messagesQuery = useQuery({
    queryKey: ["conversation-view", "messages", conversation.id],
    queryFn: () =>
      apiResolver<{ chatMessages: (Message & { chatMessageId: string })[] }>(
        api.get(
          `/sessions-api/quackchat/tenants/${selectedWorkspace}/chat-sessions/${conversation.displayId}/messages`
        )
      ),
    select: (data) => {
      return data.chatMessages.map((message) => ({
        ...message,
        deduplicationKey: message?.chatMessageId ?? message.deduplicationKey,
      }));
    },
  });

  const sourcesQuery = useQuery({
    queryKey: ["conversation-view", "sources", conversation.id],
    queryFn: () =>
      apiResolver<Source[]>(
        api.get(
          `/sessions-api/chat-session/sessions/${conversation.displayId}/sources`
        )
      ),
  });

  const suggestedReplyAuiApi = useSuggestedReplyAuiApi();

  const agentMessageIdsWithCot = useMemo(() => {
    const messages = messagesQuery.data ?? [];
    const sources = sourcesQuery.data ?? [];
    if (!suggestedReplyAui) return [];
    return messages
      .filter(
        (m) =>
          m.author !== "USER" &&
          sources.some(
            (s) =>
              s.chatMessageId === m.chatMessageId &&
              s.chainOfThoughtSteps?.length
          )
      )
      .map((m) => m.chatMessageId);
  }, [
    messagesQuery.data,
    sourcesQuery.data,
    suggestedReplyAui,
  ]);

  const auiMessageQueries = useQueries(
    agentMessageIdsWithCot.map((chatMessageId) => ({
      queryKey: ["aui-message", chatMessageId] as const,
      queryFn: () =>
        suggestedReplyAuiApi.getAuiMessageByChatMessageId(chatMessageId),
      enabled: suggestedReplyAui && !!chatMessageId,
    }))
  );

  // useQueries returns a fresh array reference on every render, so we key the
  // memo on a content-derived string to keep this map (and every memo that
  // depends on it) stable until the actual AUI bodies change. Without this
  // the transcript's auto-scroll-to-bottom effect fires on every render.
  const auiBodiesKey = auiMessageQueries
    .map((q) => (q.data as AuiChatMessage | null | undefined)?.body ?? "")
    .join("\u0000");

  const auiBodyByMessageId = useMemo(() => {
    const map: Record<string, string> = {};
    auiMessageQueries.forEach((result, i) => {
      const id = agentMessageIdsWithCot[i];
      const data = result.data as AuiChatMessage | null | undefined;
      const body = data?.body;
      if (id && body) {
        map[id] = body;
      }
    });
    return map;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [agentMessageIdsWithCot, auiBodiesKey]);

  useEffect(() => {
    if (messagesQuery.data && onMessagesLoaded) {
      onMessagesLoaded(messagesQuery.data);
    }
  }, [messagesQuery.data, onMessagesLoaded]);

  // Helper to preserve scroll position during state changes
  const preserveScrollPosition = useCallback((callback: () => void) => {
    // Save current scroll position
    if (scrollContainerRef.current) {
      scrollPositionRef.current = scrollContainerRef.current.scrollTop;
    }

    // Execute the callback
    callback();

    // Restore scroll position after React re-renders
    requestAnimationFrame(() => {
      if (scrollContainerRef.current && scrollPositionRef.current > 0) {
        scrollContainerRef.current.scrollTop = scrollPositionRef.current;
      }
    });
  }, []);

  const handleTranslateAll = useCallback(
    async (enabled: boolean) => {
      if (!enabled) {
        preserveScrollPosition(() => {
          setTranslateAll(false);
        });
        return;
      }

      preserveScrollPosition(() => {
        setIsBatchTranslating(true);
        setTranslateAll(true);
      });

      // Get untranslated messages
      const untranslated = (messagesQuery.data ?? [])
        .filter((m) => !translations.has(m.text))
        .map((m) => m.text);

      // Translate in parallel with error handling
      const results = await Promise.allSettled(
        untranslated.map((text) =>
          translateText(text).then((translated) => ({ text, translated }))
        )
      );

      // Update cache with successful translations
      preserveScrollPosition(() => {
        setTranslations((prev) => {
          const newMap = new Map(prev);
          results.forEach((result) => {
            if (result.status === "fulfilled") {
              newMap.set(result.value.text, result.value.translated);
            }
          });
          return newMap;
        });
      });

      const failedCount = results.filter((r) => r.status === "rejected").length;
      if (failedCount > 0) {
        toast.error(`Failed to translate ${failedCount} message(s)`);
      }

      setIsBatchTranslating(false);
    },
    [messagesQuery.data, translations, preserveScrollPosition]
  );

  const chatSettings = useMemo<ChatSettings>(() => {
    if (configuration == null) {
      return defaultChatSettings;
    }

    const { customSVG, closeSVG, avatarSVG, userAvatarSVG } =
      configuration.chatSettings;

    return {
      ...defaultChatSettings,
      ...configuration.chatSettings,
      customSVG: `${params.SDK_CONFIG}${customSVG}`,
      ...(avatarSVG != null
        ? { avatarSVG: `${params.SDK_CONFIG}${avatarSVG}` }
        : {}),
      ...(closeSVG != null
        ? { closeSVG: `${params.SDK_CONFIG}${closeSVG}` }
        : {}),
      ...(userAvatarSVG != null
        ? { userAvatarSVG: `${params.SDK_CONFIG}${userAvatarSVG}` }
        : {}),
    };
  }, [configuration]);

  const displayMessages = useMemo(() => {
    if (!messagesQuery.data) return [];

    return messagesQuery.data.map((message) => {
      const shouldShowTranslation =
        translateAll && translations.has(message.text);
      let displayText = shouldShowTranslation
        ? translations.get(message.text) ?? message.text
        : message.text;

      if (
        suggestedReplyAui &&
        message.author !== "USER" &&
        auiMockMessageIds.has(message.chatMessageId)
      ) {
        const auiBody = auiBodyByMessageId[message.chatMessageId];
        if (auiBody) {
          displayText = auiBody;
        }
      }

      return {
        ...message,
        text: displayText,
      };
    });
  }, [
    messagesQuery.data,
    translateAll,
    translations,
    suggestedReplyAui,
    auiMockMessageIds,
    auiBodyByMessageId,
  ]);

  return (
    <WalkthroughProvider>
      <Flex
        height={"100%"}
        direction={"column"}
        pt={onTrain ? undefined : "6"}
        p={"2"}
        overflow={"hidden"}
      >
        {!onTrain && (
          <Flex
            px={"2"}
            pb={"2"}
            justify={"between"}
            align={"center"}
            style={{
              borderBottom: "1px solid var(--gray-6)",
            }}
          >
            <Flex align={"center"} gap={"2"}>
              <PiTranslate
                style={{ color: "var(--gray-11)", fontSize: "18px" }}
              />
              <span
                style={{
                  fontSize: "14px",
                  fontWeight: 500,
                  color: "var(--gray-12)",
                }}
              >
                Translate All
              </span>
              <Switch
                size="2"
                disabled={isBatchTranslating}
                checked={translateAll}
                onCheckedChange={handleTranslateAll}
              />
              {isBatchTranslating && <Spinner />}
            </Flex>
          </Flex>
        )}

        <Messages
          messages={displayMessages}
          customSVG={chatSettings.customSVG}
          avatarSVG={chatSettings.avatarSVG}
          userAvatarSVG={chatSettings.userAvatarSVG}
          svgRadius={chatSettings.svgRadius}
          showCSAT={false}
          csatType={chatSettings.csatType}
          allowCsatComment={false}
          csatCommentText={chatSettings.csatCommentText}
          thinking={isLoading}
          csatScore={0}
          csatComment={""}
          onCSATScore={() => {}}
          onEscalate={async () => {}}
          onEscalationEmail={async () => {}}
          onFirstEscalationEmail={async () => {}}
          tenant={""}
          quackAgentName={""}
          chatEnded={false}
          sendFormOptionSelected={async () => {}}
        />

        {messagesQuery.data?.map((message, index) => {
          const source = sourcesQuery.data?.find(
            (source) => source.chatMessageId === message.chatMessageId
          );

          return (
            <MessageActions
              key={message.chatMessageId}
              chatMessageId={message.chatMessageId}
              source={source}
              isAgent={message.author !== "USER"}
              isFirst={index === 0}
              onTrain={
                onTrain
                  ? () => onTrain(message.chatMessageId, message.text)
                  : undefined
              }
              onTrainUpTo={
                chooseMessagesUpTo
                  ? () =>
                      chooseMessagesUpTo(
                        message.chatMessageId,
                        messagesQuery.data
                      )
                  : undefined
              }
              onChainOfThoughtsClick={onChainOfThoughtsClick}
              isAuiMock={auiMockMessageIds.has(message.chatMessageId)}
              onToggleAuiMock={() => toggleAuiMock(message.chatMessageId)}
              hasAuiResponse={!!auiBodyByMessageId[message.chatMessageId]}
            />
          );
        })}
      </Flex>
    </WalkthroughProvider>
  );
}
